import React from 'react';
import { formatSaleDate } from '@repo/checkout/lib/formatSaleDate';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';
import useCheckoutState from '@repo/checkout/hooks/useCheckoutState';

function TicketTypeSaleDate({
  ticketType,
  ticketGroup,
  allTicketGroups = [],
  allTicketTypes = [],
}) {
  const { page, theme, currencyLocale } = useCheckoutState();
  const labels = getThemeLabels(theme);
  const now = new Date();

  // Date-based behaviors that we need to process
  const dateBasedBehaviors = [
    'until_event_starts',
    'date_time_based',
    'after_ticket_sold_out',
    'after_ticket_sale_ends',
    'after_ticket_ends_or_sold_out',
  ];

  // Get dates from ticketGroup if it has date-based behavior
  const groupDates = getTicketGroupDates(
    page,
    ticketGroup,
    allTicketGroups,
    dateBasedBehaviors,
    now,
  );

  // Get dates from ticketType if it has date-based behavior
  const typeDates = getTicketTypeDates(page, ticketType, allTicketTypes, dateBasedBehaviors, now);

  // If neither has date-based behavior, return null
  if (!groupDates && !typeDates) {
    return null;
  }

  // Combine all possible dates and find the most restrictive
  const allStartDates = [groupDates?.startDate, typeDates?.startDate].filter(Boolean);
  const allEndDates = [groupDates?.endDate, typeDates?.endDate].filter(Boolean);

  // Latest start date (most restrictive)
  const effectiveStart =
    allStartDates.length > 0 ? new Date(Math.max(...allStartDates.map(d => d.getTime()))) : null;

  // Earliest end date (most restrictive)
  const effectiveEnd =
    allEndDates.length > 0 ? new Date(Math.min(...allEndDates.map(d => d.getTime()))) : null;

  const formattedDate = formatSaleDate(effectiveStart, effectiveEnd, {
    now,
    timezone: page.eventDetails?.timezone,
    locale: currencyLocale,
    showTimezone: ticketGroup?.layout?.showTicketSaleDateTimezone,
    startText: labels.startDateText,
    endText: labels.endDateText,
    endedText: labels.endedAtDateText,
    showStartedOn: false,
  });

  return (
    <div
      className={[
        'cp_ticket-type-sale-date',
        'text-[var(--cp_checkout-ticketGroups-ticketTypes-saleDate-color)]',
        '[font-family:var(--cp_checkout-ticketGroups-ticketTypes-saleDate-font-family)]',
        '[font-style:var(--cp_checkout-ticketGroups-ticketTypes-saleDate-font-style)]',
        '[font-size:var(--cp_checkout-ticketGroups-ticketTypes-saleDate-font-size)]',
        '[line-height:var(--cp_checkout-ticketGroups-ticketTypes-saleDate-line-height)]',
        '[font-weight:var(--cp_checkout-ticketGroups-ticketTypes-saleDate-font-weight)]',
      ].join(' ')}
    >
      {formattedDate}
    </div>
  );
}

// Function to get dates from a ticket group based on its availability behavior
function getTicketGroupDates(page, ticketGroup, allTicketGroups, dateBasedBehaviors) {
  if (!ticketGroup || !dateBasedBehaviors.includes(ticketGroup.availabilityBehavior)) {
    return null;
  }

  const availabilityBehavior = ticketGroup.availabilityBehavior;

  switch (availabilityBehavior) {
    case 'until_event_starts': {
      const pageStartTime = page?.eventDetails?.startDate
        ? new Date(page?.eventDetails?.startDate)
        : null;
      return { startDate: null, endDate: pageStartTime };
    }
    case 'date_time_based': {
      return {
        startDate: ticketGroup.saleStartOn ? new Date(ticketGroup.saleStartOn) : null,
        endDate: ticketGroup.saleEndOn ? new Date(ticketGroup.saleEndOn) : null,
      };
    }
    case 'after_ticket_sale_ends': {
      if (ticketGroup.triggerTicketGroup) {
        const referencedGroup = allTicketGroups.find(
          tg => tg._id === ticketGroup.triggerTicketGroup,
        );
        if (referencedGroup && referencedGroup.status === 'enabled' && referencedGroup.saleEndOn) {
          return {
            startDate: new Date(referencedGroup.saleEndOn),
            endDate: ticketGroup.saleEndOn ? new Date(ticketGroup.saleEndOn) : null,
          };
        }
      }

      return {
        startDate: null,
        endDate: ticketGroup.saleEndOn ? new Date(ticketGroup.saleEndOn) : null,
      };
    }
    case 'after_ticket_sold_out': {
      if (ticketGroup.triggerTicketGroup) {
        const referencedGroup = allTicketGroups.find(
          tg => tg._id === ticketGroup.triggerTicketGroup,
        );
        if (referencedGroup && referencedGroup.status === 'enabled') {
          if (referencedGroup.isSoldOut) {
            return null;
          }

          return {
            startDate: null, // Starts immediately when trigger sells out
            endDate: ticketGroup.saleEndOn ? new Date(ticketGroup.saleEndOn) : null,
          };
        }
      }

      return {
        startDate: null,
        endDate: ticketGroup.saleEndOn ? new Date(ticketGroup.saleEndOn) : null,
      };
    }
    case 'after_ticket_ends_or_sold_out': {
      if (ticketGroup.triggerTicketGroup) {
        const referencedGroup = allTicketGroups.find(
          tg => tg._id === ticketGroup.triggerTicketGroup,
        );
        if (referencedGroup && referencedGroup.status === 'enabled') {
          if (referencedGroup.isSoldOut) {
            return null;
          }

          return {
            startDate: new Date(referencedGroup.saleEndOn),
            endDate: ticketGroup.saleEndOn ? new Date(ticketGroup.saleEndOn) : null,
          };
        }
      }

      return {
        startDate: null,
        endDate: ticketGroup.saleEndOn ? new Date(ticketGroup.saleEndOn) : null,
      };
    }
  }

  return null;
}

// Function to get dates from a ticket type based on its availability behavior
function getTicketTypeDates(page, ticketType, allTicketTypes, dateBasedBehaviors) {
  if (!ticketType || !dateBasedBehaviors.includes(ticketType.availabilityBehavior)) {
    return null;
  }

  const availabilityBehavior = ticketType.availabilityBehavior;

  switch (availabilityBehavior) {
    case 'until_event_starts': {
      const pageStartTime = page?.eventDetails?.startDate
        ? new Date(page?.eventDetails?.startDate)
        : null;
      return { startDate: null, endDate: pageStartTime };
    }
    case 'date_time_based': {
      return {
        startDate: ticketType.saleStartOn ? new Date(ticketType.saleStartOn) : null,
        endDate: ticketType.saleEndOn ? new Date(ticketType.saleEndOn) : null,
      };
    }
    case 'after_ticket_sold_out': {
      if (ticketType.triggerTicketType) {
        const referencedType = allTicketTypes.find(tt => tt._id === ticketType.triggerTicketType);
        if (referencedType && referencedType.status === 'enabled') {
          return {
            startDate: null, // Starts immediately when trigger sells out
            endDate: ticketType.saleEndOn ? new Date(ticketType.saleEndOn) : null,
          };
        }
      }

      return {
        startDate: null,
        endDate: ticketType.saleEndOn ? new Date(ticketType.saleEndOn) : null,
      };
    }
    case 'after_ticket_sale_ends': {
      if (ticketType.triggerTicketType) {
        const referencedType = allTicketTypes.find(tt => tt._id === ticketType.triggerTicketType);
        if (referencedType && referencedType.status === 'enabled' && referencedType.saleEndOn) {
          return {
            startDate: new Date(referencedType.saleEndOn),
            endDate: ticketType.saleEndOn ? new Date(ticketType.saleEndOn) : null,
          };
        }
      }

      return {
        startDate: null,
        endDate: ticketType.saleEndOn ? new Date(ticketType.saleEndOn) : null,
      };
    }
    case 'after_ticket_ends_or_sold_out': {
      if (ticketType.triggerTicketType) {
        const referencedType = allTicketTypes.find(tt => tt._id === ticketType.triggerTicketType);
        if (referencedType && referencedType.status === 'enabled') {
          if (referencedType.isSoldOut) {
            return {
              startDate: null,
              endDate: ticketType.saleEndOn ? new Date(ticketType.saleEndOn) : null,
            };
          }

          return {
            startDate: new Date(referencedType.saleEndOn),
            endDate: ticketType.saleEndOn ? new Date(ticketType.saleEndOn) : null,
          };
        }
      }

      return {
        startDate: null,
        endDate: ticketType.saleEndOn ? new Date(ticketType.saleEndOn) : null,
      };
    }
  }

  return null;
}

export default TicketTypeSaleDate;
